import { type AITrace } from "@inrange/building-manager-api-client";
import { Table } from "react-bootstrap";
import DemandFileSummaryRow, { HhDataTempFile } from "./DemandFileSummaryRow";

interface FileSummaryTableProps {
  files: HhDataTempFile[];
  showActions?: boolean;
  onProcessClick?: (file: HhDataTempFile) => void;
  onDeleteClick?: (file: HhDataTempFile) => void;
  onCommitClick?: (file: HhDataTempFile) => void;
  selectedFiles?: HhDataTempFile[];
  onSelectionChange?: (files: HhDataTempFile[]) => void;
  // Per-row state set by the bulk Auto-process flow in DemandData. Undefined when
  // the row isn't currently being tracked by a bulk run.
  autoProcessingGuids?: Set<string>;
  autoFailedGuids?: Set<string>;
  // Newer sub-state Sets for the failure -> AI analysis -> auto-fix UX. A guid in
  // autoAnalysingGuids is a failed file whose AI analysis is currently running.
  // A guid in autoReprocessingGuids is a failed file whose AI proposed a fix that
  // we auto-applied via /process - waiting on the reprocess LRO.
  autoAnalysingGuids?: Set<string>;
  autoReprocessingGuids?: Set<string>;
  // AI analysis result per failed fileGuid - lets the row render the
  // [Review file analysis] / [Reprocess with proposed fix] buttons once the
  // analysis returns.
  autoAnalysisResults?: Map<
    string,
    {
      summary: string;
      details: string;
      proposed_config?: Record<string, unknown> | null;
      proposed_config_rationale?: string | null;
      trace?: AITrace | null;
    }
  >;
  // Guids where an AI fix was already auto-applied once (drives "fix attempted and
  // still failed" wording). Guids successfully healed by an AI fix (green badge).
  autoFixAttemptedGuids?: Set<string>;
  autoFixedGuids?: Set<string>;
  // Per-file attempt counter (informational "(attempt N)" on the badge).
  autoAttemptCounts?: Map<string, number>;
  // Guids that /process_auto flagged as multi-curve (one file -> several curves).
  // These are parked pending an operator split, not processed.
  autoMultiCurveGuids?: Set<string>;
  onReviewAnalysis?: (file: HhDataTempFile) => void;
  onReprocessWithProposedFix?: (file: HhDataTempFile) => void;
  onReviewSplit?: (file: HhDataTempFile) => void;
}

const FileSummaryTable = ({
  files,
  showActions = true,
  onProcessClick,
  onDeleteClick,
  onCommitClick,
  selectedFiles = [],
  onSelectionChange,
  autoProcessingGuids,
  autoFailedGuids,
  autoAnalysingGuids,
  autoReprocessingGuids,
  autoAnalysisResults,
  autoFixAttemptedGuids,
  autoFixedGuids,
  autoAttemptCounts,
  autoMultiCurveGuids,
  onReviewAnalysis,
  onReprocessWithProposedFix,
  onReviewSplit,
}: FileSummaryTableProps) => {
  const handleSelectAll = (checked: boolean) => {
    if (onSelectionChange) {
      onSelectionChange(checked ? files : []);
    }
  };

  const handleSelectFile = (file: HhDataTempFile, checked: boolean) => {
    if (onSelectionChange) {
      if (checked) {
        onSelectionChange([...selectedFiles, file]);
      } else {
        onSelectionChange(
          selectedFiles.filter((f) => f.fileGuid !== file.fileGuid)
        );
      }
    }
  };

  const allSelected = files.length > 0 && selectedFiles.length === files.length;
  const someSelected =
    selectedFiles.length > 0 && selectedFiles.length < files.length;

  return (
    <Table bordered size="sm" responsive style={{ marginTop: "8px" }}>
      <thead>
        <tr>
          {onSelectionChange && (
            <th style={{ width: "40px" }}>
              <input
                type="checkbox"
                checked={allSelected}
                ref={(input) => {
                  if (input) {
                    input.indeterminate = someSelected;
                  }
                }}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            </th>
          )}
          <th>File Name</th>
          <th>Uploaded At</th>
          <th>Processed at</th>
          <th>Input Date Range</th>
          <th>Selected Date Range</th>
          <th>Typical Year kWh</th>
          <th>Customer Data %</th>
          {showActions && <th>Actions</th>}
        </tr>
      </thead>
      <tbody>
        {files.map((file) => {
          // Derive the row phase. Order matters: in-flight phases take precedence
          // over terminal phases. The `fixAttempted` flag distinguishes a first-time
          // analysis from a re-analysis after an auto-fix already failed, which
          // drives the wording. A healed file (outputMetaData) shows the green
          // auto-fixed badge if it got there via an AI fix, else no badge.
          const fixAttempted =
            autoFixAttemptedGuids?.has(file.fileGuid) ?? false;
          let autoStatus:
            | "processing"
            | "analysing"
            | "reanalysing"
            | "reprocessing"
            | "failed_with_analysis"
            | "failed_with_updated_fix"
            | "failed_no_analysis"
            | "autofixed"
            | "multi_curve"
            | undefined;
          if (file.outputMetaData) {
            autoStatus = autoFixedGuids?.has(file.fileGuid)
              ? "autofixed"
              : undefined;
          } else if (autoMultiCurveGuids?.has(file.fileGuid)) {
            autoStatus = "multi_curve";
          } else if (autoProcessingGuids?.has(file.fileGuid)) {
            autoStatus = "processing";
          } else if (autoReprocessingGuids?.has(file.fileGuid)) {
            autoStatus = "reprocessing";
          } else if (autoAnalysingGuids?.has(file.fileGuid)) {
            autoStatus = fixAttempted ? "reanalysing" : "analysing";
          } else if (autoFailedGuids?.has(file.fileGuid)) {
            if (autoAnalysisResults?.has(file.fileGuid)) {
              autoStatus = fixAttempted
                ? "failed_with_updated_fix"
                : "failed_with_analysis";
            } else {
              autoStatus = "failed_no_analysis";
            }
          }
          const analysis = autoAnalysisResults?.get(file.fileGuid);
          return (
            <DemandFileSummaryRow
              key={file.fileGuid}
              file={file}
              onProcessClick={onProcessClick}
              onDeleteClick={onDeleteClick}
              onCommitClick={onCommitClick}
              showActions={showActions}
              isSelected={selectedFiles.some(
                (f) => f.fileGuid === file.fileGuid
              )}
              onSelectionChange={
                onSelectionChange
                  ? (checked) => handleSelectFile(file, checked)
                  : undefined
              }
              autoStatus={autoStatus}
              hasProposedFix={!!analysis?.proposed_config}
              hasAnalysis={!!analysis}
              attemptCount={autoAttemptCounts?.get(file.fileGuid)}
              onReviewAnalysis={
                onReviewAnalysis ? () => onReviewAnalysis(file) : undefined
              }
              onReprocessWithProposedFix={
                onReprocessWithProposedFix
                  ? () => onReprocessWithProposedFix(file)
                  : undefined
              }
              onReviewSplit={
                onReviewSplit ? () => onReviewSplit(file) : undefined
              }
            />
          );
        })}
      </tbody>
    </Table>
  );
};

export default FileSummaryTable;
